import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertOctagon, Loader2, CheckCircle2, XCircle, DollarSign, MessageCircle, ExternalLink, Phone, MapPin, Clock } from "lucide-react";
import { formatGNF } from "@/utils/format";

const REASON_LABEL = {
  client_injoignable: "Client injoignable",
  adresse_introuvable: "Adresse introuvable",
  cash_non_paye: "Cash non payé",
  commande_refusee: "Commande refusée",
  autre: "Autre",
  plat_manquant: "Plat manquant",
  commande_sans_livreur: "Commande sans livreur",
};

const STATUS_LABEL = {
  a_traiter: "À traiter",
  en_cours: "En cours",
  resolu: "Résolu",
};

const STATUS_COLOR = {
  a_traiter: "#E32636",
  en_cours: "#F4C430",
  resolu: "#009E60",
};

export default function AdminIncidentsPanel() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("a_traiter");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/incidents", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="admin-incidents-panel">
      <div className="flex flex-wrap gap-2">
        {[
          { k: "a_traiter", label: "À traiter" },
          { k: "en_cours", label: "En cours" },
          { k: "resolu", label: t("admin.aipResolvedF") },
          { k: "", label: "Tous" },
        ].map((s) => (
          <button
            key={s.k || "all"}
            onClick={() => setStatusFilter(s.k)}
            data-testid={`incidents-filter-${s.k || "all"}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold border ${statusFilter === s.k ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
          >
            {s.label}
          </button>
        ))}
        <button onClick={refresh} data-testid="incidents-refresh" className="ml-auto text-xs text-mute hover:text-ink inline-flex items-center gap-1">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />} Actualiser
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-10 text-center text-mute">
          <AlertOctagon className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun incident {STATUS_LABEL[statusFilter]?.toLowerCase() || ""} pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((inc) => (
            <IncidentCard key={inc.incident_id} inc={inc} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function IncidentCard({ inc, onChanged }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [action, setAction] = useState("");
  const [note, setNote] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const isOpen = inc.status !== "resolu";
  const shortId = inc.incident_id.slice(-6).toUpperCase();
  const orderShort = inc.order_id.slice(-6).toUpperCase();

  const take = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/admin/incidents/${inc.incident_id}/take`);
      toast.success(t("admin.aipTaken"));
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const arbitrate = async () => {
    if (!action) { toast.error(t("admin.aipChooseAction")); return; }
    if (action === "mark_cancelled" && !cancelReason.trim()) { toast.error(t("admin.aipCancelReasonRequired")); return; }
    setBusy(true);
    try {
      await apiClient.patch(`/admin/incidents/${inc.incident_id}/arbitrate`, {
        action, note: note.trim(), cancel_reason: action === "mark_cancelled" ? cancelReason.trim() : undefined,
      });
      toast.success(t("admin.aipArbitrated"));
      setShowResolve(false);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`bg-white rounded-2xl p-5 border-2 ${isOpen ? "border-brand-red/30" : "border-sand"}`} data-testid={`incident-card-${inc.incident_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-mute font-semibold">Ticket #{shortId}</div>
          <div className="font-display text-lg font-semibold">
            {REASON_LABEL[inc.reason] || inc.reason}
          </div>
          <div className="text-xs text-mute">
            Commande <span className="font-mono">#{orderShort}</span> · {inc.restaurant_name || "?"} ·
            {" "}{new Date(inc.reported_at).toLocaleString("fr-FR")}
          </div>
        </div>
        <span
          className="rounded-full px-3 py-1 text-xs font-semibold"
          style={{ backgroundColor: STATUS_COLOR[inc.status] + "22", color: STATUS_COLOR[inc.status] }}
          data-testid={`incident-status-${inc.incident_id}`}
        >
          {STATUS_LABEL[inc.status]}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
        <div className="flex items-center gap-2 text-mute">
          <Phone className="w-3.5 h-3.5" /> Client : <span className="text-ink font-mono">{inc.client_phone || "—"}</span>
        </div>
        <div className="flex items-center gap-2 text-mute">
          <Phone className="w-3.5 h-3.5" /> Livreur : <span className="text-ink">{inc.driver_name || "—"}{inc.driver_phone ? ` · ${inc.driver_phone}` : ""}</span>
        </div>
        <div className="flex items-start gap-2 text-mute sm:col-span-2">
          <MapPin className="w-3.5 h-3.5 mt-0.5" /> <span className="text-ink">{inc.client_address || "—"}</span>
        </div>
        <div className="flex items-center gap-2 text-mute">
          <DollarSign className="w-3.5 h-3.5" /> Montant : <span className="text-ink font-semibold">{formatGNF(inc.amount)}</span>
          <span className="text-xs">(dont plats {formatGNF(inc.subtotal || 0)})</span>
        </div>
        <div className="flex items-center gap-2 text-mute">
          <Clock className="w-3.5 h-3.5" /> Paiement : <span className="text-ink">{inc.payment_method}</span>
          {inc.called_client && <span className="text-brand-green">· ✓ {t("admin.aipClientCalled")}</span>}
        </div>
      </div>

      {inc.comment && (
        <div className="rounded-xl bg-elevated p-3 text-sm text-mute mb-3">
          <span className="text-ink font-semibold">Commentaire livreur :</span> {inc.comment}
        </div>
      )}

      {inc.whatsapp_support_link && (
        <a
          href={inc.whatsapp_support_link}
          target="_blank" rel="noreferrer"
          data-testid={`incident-wa-${inc.incident_id}`}
          className="inline-flex items-center gap-1.5 text-xs text-brand-green font-semibold mb-3"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Voir contexte WhatsApp support
          <ExternalLink className="w-3 h-3" />
        </a>
      )}

      {inc.resolution && (
        <div className="rounded-xl bg-[#E7F6EE] border border-brand-green/30 p-3 text-sm mb-3">
          <div className="font-semibold text-brand-green">Résolu : {inc.resolution.action}</div>
          {inc.resolution.note && <div className="text-mute text-xs mt-1">« {inc.resolution.note} »</div>}
          {inc.resolution.cancel_reason && <div className="text-mute text-xs mt-1">Motif annul. : {inc.resolution.cancel_reason}</div>}
        </div>
      )}

      {isOpen && (
        <>
          {inc.status === "a_traiter" && (
            <button
              onClick={take}
              disabled={busy}
              data-testid={`incident-take-${inc.incident_id}`}
              className="mr-2 inline-flex items-center gap-1.5 rounded-full border border-sand hover:border-brand-red hover:text-brand-red px-4 py-2 text-sm font-semibold"
            >
              Prendre en charge
            </button>
          )}
          <button
            onClick={() => setShowResolve((v) => !v)}
            data-testid={`incident-resolve-toggle-${inc.incident_id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white px-4 py-2 text-sm font-semibold"
          >
            Arbitrer
          </button>
        </>
      )}

      {showResolve && isOpen && (
        <div className="mt-4 border-t border-sand pt-4 space-y-3" data-testid={`incident-arbitrate-form-${inc.incident_id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <ActionButton
              current={action} value="mark_delivered" onClick={() => setAction("mark_delivered")}
              icon={<CheckCircle2 className="w-4 h-4" />} label={t("admin.aipMarkDelivered")} color="green"
              testid={`arb-delivered-${inc.incident_id}`}
            />
            <ActionButton
              current={action} value="mark_cancelled" onClick={() => setAction("mark_cancelled")}
              icon={<XCircle className="w-4 h-4" />} label={t("admin.aipMarkCancelled")} color="red"
              testid={`arb-cancelled-${inc.incident_id}`}
            />
            <ActionButton
              current={action} value="cash_ledger" onClick={() => setAction("cash_ledger")}
              icon={<DollarSign className="w-4 h-4" />} label="Litige cash" color="ink"
              testid={`arb-ledger-${inc.incident_id}`}
            />
          </div>
          {action === "mark_cancelled" && (
            <input
              value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
              placeholder={t("admin.aipCancelPh")}
              data-testid={`arb-cancel-reason-${inc.incident_id}`}
              className="w-full bg-white border border-sand rounded-xl px-4 py-2.5 text-sm outline-none focus:border-brand-red"
            />
          )}
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={t("admin.aipNotePh")} rows={2}
            data-testid={`arb-note-${inc.incident_id}`}
            className="w-full bg-white border border-sand rounded-xl px-4 py-2 text-sm outline-none focus:border-brand-red"
          />
          <div className="flex gap-2">
            <button
              onClick={() => setShowResolve(false)}
              className="rounded-full border border-sand px-4 py-2 text-sm font-semibold"
            >Annuler</button>
            <button
              onClick={arbitrate}
              disabled={busy || !action}
              data-testid={`arb-submit-${inc.incident_id}`}
              className="rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Valider l'arbitrage
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionButton({ current, value, onClick, icon, label, color, testid }) {
  const active = current === value;
  const bg = active
    ? (color === "green" ? "bg-brand-green text-white border-brand-green"
      : color === "red" ? "bg-brand-red text-white border-brand-red"
      : "bg-ink text-white border-ink")
    : "bg-white border-sand hover:border-brand-red";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-xl border py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${bg}`}
    >
      {icon} {label}
    </button>
  );
}
